export type CompletionNextInfo = {
  hasNext: boolean;
  nextLabel: string | null;
  nextPageId: string | null;
  /** `true` → “Start Next Task”; `false` → “Continue” (matches prototype `isAction`). */
  isAction: boolean;
};

const DEFAULT: CompletionNextInfo = {
  hasNext: false,
  nextLabel: null,
  nextPageId: null,
  isAction: true,
};

function isSafePageId(id: string): boolean {
  return /^[a-zA-Z0-9_-]+$/.test(id);
}

/**
 * Next Learning Center task after the current checklist `pageId`, by `order`.
 * Used after “Mark Task Complete” → celebration → completion modal.
 */
/** Shape needed from `useTasklist().learningCenterTasks`. */
export type LearningCenterTaskNavRow = {
  title: string;
  taskType: 'anytime' | 'actionable';
  order: number;
  pageId?: string;
  /** When set, `/tasklist/page/{pathSegment}` is preferred over numeric `pageId` in the URL. */
  pathSegment?: string;
};

export function getLearningCenterCompletionNext(
  currentPageId: string | undefined,
  learningCenterTasks: LearningCenterTaskNavRow[]
): CompletionNextInfo {
  if (!currentPageId?.trim()) return DEFAULT;
  const pid = String(currentPageId).trim();
  const sorted = [...learningCenterTasks].sort((a, b) => a.order - b.order);
  const idx = sorted.findIndex((t) => String(t.pageId ?? '').trim() === pid);
  if (idx === -1) return DEFAULT;

  for (let j = idx + 1; j < sorted.length; j++) {
    const next = sorted[j];
    const nextPid = next.pageId?.trim();
    if (nextPid && isSafePageId(nextPid)) {
      return {
        hasNext: true,
        nextLabel: next.title,
        nextPageId: nextPid,
        isAction: next.taskType === 'actionable',
      };
    }
  }
  return DEFAULT;
}

/** Hero `tasklist.json` rows with JSON secondary pages (Setup and Access, School Documents). */
const HERO_SECONDARY_PAGE_IDS = new Set(['100', '200']);

export type TasklistDataNavRow = {
  checklistId: number;
  description: string;
  pageId?: string;
};

export type HeroTaskNavRow = {
  id: number;
  completed: boolean;
};

/**
 * After completing a hero secondary page (`pageId` 100 or 200), the completion modal should:
 * - Offer the **other** hero task if it is still incomplete (same order as `tasklist.json` tasks).
 * - Offer **Return Home** only when both hero tasks are complete.
 *
 * Returns `null` when `currentPageId` is not one of those hero pages (caller uses Learning Center chain).
 */
export function getHeroPairCompletionModalNext(
  currentPageId: string | undefined,
  tasklistData: TasklistDataNavRow[],
  heroTasks: HeroTaskNavRow[],
  visibleHeroPageIds?: Set<string>
): CompletionNextInfo | null {
  const pid = String(currentPageId ?? '').trim();
  if (!pid || !HERO_SECONDARY_PAGE_IDS.has(pid)) return null;

  const isVisibleHeroPage = (pageId: string) =>
    !visibleHeroPageIds || visibleHeroPageIds.has(pageId);

  const rows = tasklistData
    .filter((c) => {
      const p = String(c.pageId ?? '').trim();
      return HERO_SECONDARY_PAGE_IDS.has(p);
    })
    .map((c) => {
      const pageId = String(c.pageId ?? '').trim();
      const h = heroTasks.find((x) => Number(x.id) === Number(c.checklistId));
      return {
        pageId,
        title: c.description || 'Untitled',
        completed: Boolean(h?.completed),
        checklistId: Number(c.checklistId),
      };
    })
    .filter((r) => r.pageId && isSafePageId(r.pageId))
    .sort((a, b) => a.checklistId - b.checklistId);

  if (rows.length === 0) return null;

  const incompleteOther = rows
    .filter((r) => !r.completed && r.pageId !== pid)
    .sort((a, b) => a.checklistId - b.checklistId);

  const incompleteVisibleOther = incompleteOther.filter((r) => isVisibleHeroPage(r.pageId));

  if (incompleteVisibleOther.length > 0) {
    const target = incompleteVisibleOther[0];
    return {
      hasNext: true,
      nextLabel: target.title,
      nextPageId: target.pageId,
      isAction: true,
    };
  }

  if (incompleteOther.some((r) => !isVisibleHeroPage(r.pageId))) {
    return null;
  }

  return DEFAULT;
}

function isOnlyVisibleHeroTaskCompletion(
  currentPageId: string | undefined,
  visibleHeroPageIds?: Set<string>
): boolean {
  const pid = String(currentPageId ?? '').trim();
  if (!pid || !HERO_SECONDARY_PAGE_IDS.has(pid)) return false;
  if (!visibleHeroPageIds || visibleHeroPageIds.size !== 1) return false;
  return visibleHeroPageIds.has(pid);
}

/**
 * Completion modal CTA: hero pair (`100` / `200`) first, then Learning Center `pageId` chain.
 * Pass role/grade-filtered task rows and `visibleHeroPageIds` so hidden tasks are skipped.
 * When "Your tasks" shows only one hero task, completing it always offers Return Home.
 */
export function getCompletionModalNext(
  currentPageId: string | undefined,
  tasklistData: TasklistDataNavRow[],
  heroTasks: HeroTaskNavRow[],
  learningCenterTasks: LearningCenterTaskNavRow[],
  visibleHeroPageIds?: Set<string>
): CompletionNextInfo {
  if (isOnlyVisibleHeroTaskCompletion(currentPageId, visibleHeroPageIds)) {
    return DEFAULT;
  }

  const hero = getHeroPairCompletionModalNext(
    currentPageId,
    tasklistData,
    heroTasks,
    visibleHeroPageIds
  );
  if (hero !== null) return hero;

  return getLearningCenterCompletionNext(currentPageId, learningCenterTasks);
}
